import { CouponMasterListConditionFormValues } from '../list/List';
import { CouponPacksQuery, useCouponPacksLazyQuery } from '../../../graphql/generated';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import Bugsnag from '@bugsnag/js';
import { OrganizationContext } from '../../../components/contexts/organization';

const PageItemsCount = 25;

export type ListCouponPack = {
  loading: boolean;
  onSearchCouponPacks: (condition: CouponMasterListConditionFormValues) => void;
  data?: CouponPacksQuery;
  onFetchMore: () => void;
};

export const useListCouponPack = (): ListCouponPack => {
  const [loading, setLoading] = useState(false);
  const { organization } = useContext(OrganizationContext);
  const { enqueueSnackbar } = useSnackbar();

  const [listQuery, { data, fetchMore, variables }] = useCouponPacksLazyQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onCompleted: () => {
      setLoading(false);
    },
    onError: (error) => {
      setLoading(false);
      enqueueSnackbar('サーバーエラーが発生した', {
        variant: 'error',
        autoHideDuration: 3000,
      });
      Bugsnag.notify(error);
    },
  });

  // 初回、検索条件なしで表示する
  useEffect(() => {
    if (!organization?.id) return;
    setLoading(true);
    listQuery({
      variables: {
        organizationId: organization?.id,
        limit: PageItemsCount,
      },
    });
  }, [listQuery, organization]);

  // 動的な検索条件をセットしてクーポンマスタを検索する
  const onSearchCouponPacks = useCallback(
    (data: CouponMasterListConditionFormValues) => {
      if (!organization?.id) return;

      setLoading(true);
      listQuery({
        variables: {
          organizationId: organization?.id,
          [data.searchCondition]: data.value,
          limit: PageItemsCount,
        },
      });
    },
    [listQuery, organization]
  );

  // さらに読み込む
  const onFetchMore = useCallback(async () => {
    if (!organization?.id) return;

    if (fetchMore && variables) {
      setLoading(true);
      await fetchMore({
        variables: {
          organizationId: organization?.id,
          nextToken: data?.couponPacks.pageInfo.nextToken,
          limit: PageItemsCount,
          serialNo: variables.serialNo,
          couponPackUrl: variables.couponPackUrl,
        },
        updateQuery: (prevQuery, { fetchMoreResult }) => {
          setLoading(false);
          if (!fetchMoreResult) {
            return prevQuery;
          }
          return {
            couponPacks: {
              __typename: fetchMoreResult.couponPacks.__typename,
              nodes: [...prevQuery.couponPacks.nodes, ...fetchMoreResult.couponPacks.nodes],
              pageInfo: fetchMoreResult.couponPacks.pageInfo,
            },
          };
        },
      });
    }
  }, [data, fetchMore, variables, organization]);

  return {
    loading,
    onSearchCouponPacks,
    data,
    onFetchMore,
  };
};
